// Axios-specific cancellation type, thrown when a request's signal is aborted. An axios major
// upgrade that renames or reshapes it has to be reflected in the catches below, or every
// superseded request starts dispatching an error - see tests/services/hardwareInventory.
import { CanceledError } from 'axios';
import { ADVISORY_JOB_POLL, GET_FIRMWARE_ADVISORIES_DEFAULT_QUERY_PARAMS } from '../../constants/firmware-advisories';
import { getApiErrorMessage } from '../../helpers/apiError';
import * as firmwareAdvisoriesService from '../../services/firmwareAdvisories';
import { describeJobOutcome, isJobSucceeded, pollJobUntilTerminal } from '../../services/jobs';
import { pushDangerNotification, pushSuccessfulNotification, pushWarningNotification } from '../notifications/actions';
import * as actionTypes from './actionTypes';
import { REVOKE_PAYLOAD, formValuesToWritePayload } from './mapper';

const UNCONFIRMED_MESSAGE = 'The advisory was submitted but is taking longer than expected to apply. Refresh the list in a moment to confirm.';

/*
 * `services/jobs` knows what a `skipped` or `suppressed` job means, not what the job was doing, so
 * the noun is supplied here.
 */
const UNAPPLIED_MESSAGE = 'The advisory was not applied';

export const showFirmwareAdvisoryFormModal = (advisory = null) => {
	return { type: actionTypes.SHOW_FIRMWARE_ADVISORY_FORM_MODAL, payload: advisory };
};

export const showSeededFirmwareAdvisoryFormModal = (seed) => {
	return { type: actionTypes.SHOW_SEEDED_FIRMWARE_ADVISORY_FORM_MODAL, payload: seed };
};

export const hideFirmwareAdvisoryFormModal = () => {
	return { type: actionTypes.HIDE_FIRMWARE_ADVISORY_FORM_MODAL };
};

export const showFirmwareAdvisoryRevokeModal = (advisory) => {
	return { type: actionTypes.SHOW_FIRMWARE_ADVISORY_REVOKE_MODAL, payload: advisory };
};

export const hideFirmwareAdvisoryRevokeModal = () => {
	return { type: actionTypes.HIDE_FIRMWARE_ADVISORY_REVOKE_MODAL };
};

export const getFirmwareAdvisories = (queryParams = GET_FIRMWARE_ADVISORIES_DEFAULT_QUERY_PARAMS) => {
	return (dispatch) => {
		dispatch({ type: actionTypes.START_FETCHING_FIRMWARE_ADVISORIES });

		return firmwareAdvisoriesService
			.getFirmwareAdvisories(queryParams)
			.then(({ data }) => {
				dispatch({
					type: actionTypes.GET_FIRMWARE_ADVISORIES_SUCCESS,
					payload: {
						advisories: data?.firmwareAdvisories ?? [],
						pagination: data?.pagination ?? {}
					}
				});

				return data?.firmwareAdvisories ?? [];
			})
			.catch((error) => {
				/*
				 * The service aborts the previous read on every new one, so a cancellation is the
				 * expected outcome of flipping the filter quickly rather than a failure.
				 */
				if (error instanceof CanceledError) {
					return;
				}

				dispatch({
					type: actionTypes.GET_FIRMWARE_ADVISORIES_ERROR,
					payload: getApiErrorMessage(error, 'The firmware advisories could not be loaded.')
				});
			});
	};
};

const isAbort = (error) => {
	return error instanceof CanceledError || error?.name === 'AbortError';
};

/*
 * An abort is the page's own unmount cleanup, so nothing is reported - but the pipeline still owns
 * a raised `saving` flag, and this slice outlives the route. Returning without dispatching leaves
 * the next visit with its row actions disabled by a write that nobody is running any more.
 */
const abandonAdvisoryWrite = (dispatch) => {
	return dispatch({ type: actionTypes.SAVE_FIRMWARE_ADVISORY_ABORTED });
};

const failAdvisoryWrite = (dispatch, message) => {
	dispatch({ type: actionTypes.SAVE_FIRMWARE_ADVISORY_ERROR, payload: message });
	dispatch(pushDangerNotification(message));
};

/*
 * The write pipeline every advisory change runs through: send, then poll the job it returns, then
 * refresh the list. It lives here rather than in a component because create, edit and revoke are
 * the same five steps, and only one of the three is a redux-form.
 *
 * Three failure shapes, deliberately handled differently:
 *
 *   Request rejected      - nothing was accepted (a 404 for an unknown component type, a 422, an
 *                           expired session). The modal stays open with the values intact so the
 *                           problem can be corrected in place.
 *   Poll never concluded  - the write WAS accepted; all that failed is watching it finish. Saying
 *                           it failed would be wrong, so this closes the modal (a filled create
 *                           form left open invites a duplicate submit over a job still running),
 *                           refreshes the list, and says the outcome is unconfirmed.
 *   Job ended un-succeeded - pollJobUntilTerminal resolves on `failed`, `canceled`, `skipped` and
 *                           `suppressed` too. It reports that the job stopped, not that it worked,
 *                           so the status is classified before anything is called a success.
 *
 * Resolves rather than rejects on a handled failure: redux-form surfaces a rejection that is not a
 * SubmissionError as an unhandled rejection, and clears `submitting` either way.
 */
const runAdvisoryWrite = async (dispatch, sendRequest, options) => {
	const { successMessage, failureMessage, queryParams, signal, hideModal } = options;
	let job;

	dispatch({ type: actionTypes.START_SAVING_FIRMWARE_ADVISORY });

	let jobId;

	try {
		const { data } = await sendRequest();

		jobId = data?.jobId;
	} catch (error) {
		if (isAbort(error)) {
			return abandonAdvisoryWrite(dispatch);
		}

		return failAdvisoryWrite(dispatch, getApiErrorMessage(error, failureMessage));
	}

	if (!jobId) {
		return failAdvisoryWrite(dispatch, 'The request was accepted but returned no job to track.');
	}

	try {
		job = await pollJobUntilTerminal(jobId, { ...ADVISORY_JOB_POLL, signal });
	} catch (error) {
		if (isAbort(error)) {
			return abandonAdvisoryWrite(dispatch);
		}

		dispatch({ type: actionTypes.SAVE_FIRMWARE_ADVISORY_ERROR, payload: UNCONFIRMED_MESSAGE });
		dispatch(pushWarningNotification(UNCONFIRMED_MESSAGE));
		dispatch(hideModal());

		return dispatch(getFirmwareAdvisories(queryParams));
	}

	if (!isJobSucceeded(job)) {
		return failAdvisoryWrite(dispatch, describeJobOutcome(job, {
			failureMessage: failureMessage,
			unappliedMessage: UNAPPLIED_MESSAGE
		}));
	}

	dispatch({ type: actionTypes.SAVE_FIRMWARE_ADVISORY_SUCCESS });
	dispatch(hideModal());
	dispatch(pushSuccessfulNotification(successMessage));

	/*
	 * The refresh is awaited inside the same promise rather than fired and forgotten, so the Save
	 * button stays disabled until the table actually shows the change.
	 */
	return dispatch(getFirmwareAdvisories(queryParams));
};

export const createFirmwareAdvisory = (values, { queryParams, signal } = {}) => {
	return (dispatch) => {
		return runAdvisoryWrite(dispatch, () => {
			return firmwareAdvisoriesService.createFirmwareAdvisory(formValuesToWritePayload(values));
		}, {
			successMessage: 'Firmware advisory created.',
			failureMessage: 'The firmware advisory could not be created.',
			queryParams: queryParams,
			signal: signal,
			hideModal: hideFirmwareAdvisoryFormModal
		});
	};
};

export const updateFirmwareAdvisory = (id, values, { queryParams, signal } = {}) => {
	return (dispatch) => {
		return runAdvisoryWrite(dispatch, () => {
			return firmwareAdvisoriesService.updateFirmwareAdvisory(id, formValuesToWritePayload(values));
		}, {
			successMessage: 'Firmware advisory updated.',
			failureMessage: 'The firmware advisory could not be updated.',
			queryParams: queryParams,
			signal: signal,
			hideModal: hideFirmwareAdvisoryFormModal
		});
	};
};

export const revokeFirmwareAdvisory = (id, { queryParams, signal } = {}) => {
	return (dispatch) => {
		return runAdvisoryWrite(dispatch, () => {
			return firmwareAdvisoriesService.updateFirmwareAdvisory(id, REVOKE_PAYLOAD);
		}, {
			successMessage: 'Firmware advisory revoked.',
			failureMessage: 'The firmware advisory could not be revoked.',
			queryParams: queryParams,
			signal: signal,
			hideModal: hideFirmwareAdvisoryRevokeModal
		});
	};
};
